import React from "react";
import { Info, Loader } from "lucide-react";
import { INCOTERM_DEF } from "./constants";

// Pending shipments & POs grouped by Incoterm (platform data)
export default function ShippingTermsOverview({ breakdown, loading, onOpenGuide }) {
    return (
        <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-sm font-bold text-white">Shipping Terms Overview</p>
                    <p className="text-[11px] text-slate-500">Pending shipments &amp; POs grouped by Incoterm</p>
                </div>
                <button
                    onClick={onOpenGuide}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-blue-500/30 text-[11px] text-blue-400 hover:bg-blue-500/10 transition-colors"
                >
                    <Info size={11}/> Reference Guide
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-16 text-slate-700">
                    <Loader size={18} className="animate-spin"/>
                </div>
            ) : breakdown.length === 0 ? (
                /* No incoterm data yet — show all standard terms as empty cards */
                <div>
                    <p className="text-[11px] text-slate-500 mb-3">
                        No Incoterm data found in shipments or POs yet.
                        Add <code className="text-blue-400 bg-[#1F2937] px-1 rounded">metadata.incoterm</code> to shipments/POs to see live counts.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {INCOTERM_DEF.map(t => (
                            <button
                                key={t.code}
                                onClick={onOpenGuide}
                                title={t.name}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1F2937] border border-[#374151] hover:border-blue-500/40 hover:bg-[#1A2540] transition-colors group"
                            >
                                <span className="text-[11px] font-black text-blue-400 tracking-wider">{t.code}</span>
                                <span className="text-[10px] text-slate-500 group-hover:text-slate-300 hidden sm:block truncate max-w-[110px]">{t.name}</span>
                                <span className="text-[10px] font-bold text-slate-600">0</span>
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {breakdown.map(t => {
                        const def = INCOTERM_DEF.find(d => d.code === t.term);
                        const pct = t.total > 0 ? Math.round((t.pending / t.total) * 100) : 0;
                        const urgentColor = t.pending > 5 ? "#EF4444" : t.pending > 2 ? "#F59E0B" : "#10B981";
                        return (
                            <button
                                key={t.term}
                                onClick={onOpenGuide}
                                title={def ? `${def.name} — ${def.desc}` : t.term}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1F2937] border border-[#374151] hover:border-blue-500/40 hover:bg-[#1A2540] transition-colors group"
                            >
                                <span className="text-[11px] font-black text-blue-400 tracking-wider">{t.term}</span>
                                <div className="flex flex-col items-start">
                                    <span className="text-[11px] font-bold" style={{color: urgentColor}}>
                                        {t.pending} pending
                                    </span>
                                    <span className="text-[9px] text-slate-600">{t.total} total · {pct}%</span>
                                </div>
                            </button>
                        );
                    })}
                    {/* Add empty placeholders for terms not in data */}
                    {INCOTERM_DEF.filter(d => !breakdown.find(b => b.term === d.code)).map(t => (
                        <button
                            key={t.code}
                            onClick={onOpenGuide}
                            title={t.name}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0D1520] border border-[#1F2937] hover:border-blue-500/30 transition-colors opacity-50"
                        >
                            <span className="text-[11px] font-black text-slate-600 tracking-wider">{t.code}</span>
                            <span className="text-[10px] text-slate-700">0</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
